import React, { useEffect, useState } from 'react';
import { Button, Card, DatePicker, Descriptions, Image, Space, Spin, Table, Tabs, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { authService } from '../services/authService';
import { normalizeFileUrl } from '../services/fileService';
import { getAllServiceCategories, type ServiceCategory } from '../services/serviceCategoryService';
import { listMeituanShops, type MeituanShop } from '../services/meituanShopService';
import type { User } from '../types';
import type {
  UserProviderOrderSettlementFlow,
  UserProviderOrderSettlementSummary,
  UserProviderSettlementFlow,
  UserProviderSettlementSummary,
} from '../services/userService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const formatMoney = (value?: number) => `¥${Number(value || 0).toFixed(2)}`;

const orderSourceTextMap: Record<string, string> = {
  wechat: '微信',
  meituan: '美团',
};

const orderSettlementStatusTextMap: Record<string, string> = {
  pending: '待入账',
  withdrawable: '已入账',
};

const orderSettlementStatusColorMap: Record<string, string> = {
  pending: 'orange',
  withdrawable: 'green',
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (num: number) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const splitIDs = (value?: string | string[]) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
};

const parseWechatRegions = (value?: string) => {
  if (!value) return [];
  try {
    const regions = JSON.parse(value);
    return Array.isArray(regions) ? regions : [];
  } catch {
    return [];
  }
};

const flattenCategories = (categories: ServiceCategory[], parentNames: string[] = []): { label: string; value: string }[] => {
  return categories.flatMap((category) => {
    const names = [...parentNames, category.name];
    const current = { label: names.join(' / '), value: category.id };
    return [current, ...flattenCategories(category.children || [], names)];
  });
};

const renderText = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') return '-';
  return value;
};

const renderFile = (url?: string) => {
  if (!url) return '-';
  const fullUrl = normalizeFileUrl(url);
  return (
    <Space orientation="vertical" size={6}>
      <Image src={fullUrl} width={120} height={80} style={{ objectFit: 'cover' }} />
      <a href={fullUrl} target="_blank" rel="noreferrer">查看原文件</a>
    </Space>
  );
};

const MyInfo: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [settlementSummary, setSettlementSummary] = useState<UserProviderSettlementSummary | null>(null);
  const [orderSettlementSummary, setOrderSettlementSummary] = useState<UserProviderOrderSettlementSummary | null>(null);
  const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});
  const [shopMap, setShopMap] = useState<Record<string, string>>({});
  const [settlementDateRange, setSettlementDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [incomeDateRange, setIncomeDateRange] = useState<[Dayjs, Dayjs] | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const profile = await authService.getUserInfo();
        setUser(profile);
        localStorage.setItem('user', JSON.stringify(profile));

        if (profile.isProvider) {
          const [settlementRes, orderSettlementRes, categoryRes, shopRes] = await Promise.allSettled([
            authService.getMyProviderSettlementFlows(),
            authService.getMyProviderOrderSettlementFlows(),
            getAllServiceCategories(),
            listMeituanShops({ page: 1, pageSize: 1000 }),
          ]);

          if (settlementRes.status === 'fulfilled') {
            setSettlementSummary(settlementRes.value);
          }
          if (orderSettlementRes.status === 'fulfilled') {
            setOrderSettlementSummary(orderSettlementRes.value);
          }
          if (categoryRes.status === 'fulfilled') {
            const nextMap: Record<string, string> = {};
            flattenCategories(categoryRes.value.data || []).forEach(item => {
              nextMap[item.value] = item.label;
            });
            setCategoryMap(nextMap);
          }
          if (shopRes.status === 'fulfilled') {
            const nextMap: Record<string, string> = {};
            (shopRes.value.data?.list || []).forEach((shop: MeituanShop) => {
              nextMap[shop.id] = `${shop.shopName || shop.opBizName || shop.shopId}（${shop.shopId}）`;
            });
            setShopMap(nextMap);
          }
        }
      } catch (error: any) {
        message.error(error?.message || '加载我的信息失败');
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, []);

  const currentUser = user;
  const serviceCategoryNames = splitIDs(currentUser?.serviceCategoryIds).map(id => categoryMap[id] || id);
  const meituanShopNames = splitIDs(currentUser?.meituanShopIds).map(id => shopMap[id] || id);
  const wechatRegions = parseWechatRegions(currentUser?.wechatServiceRegions);
  const filteredSettlementFlows = (settlementSummary?.flows || []).filter(flow => {
    if (!settlementDateRange) return true;
    const dateValue = flow.settledAt || flow.createdAt;
    const timestamp = new Date(dateValue || '').getTime();
    if (Number.isNaN(timestamp)) return false;
    return timestamp >= settlementDateRange[0].startOf('day').valueOf()
      && timestamp <= settlementDateRange[1].endOf('day').valueOf();
  });
  const filteredIncomeFlows = (orderSettlementSummary?.flows || []).filter(flow => {
    if (!incomeDateRange) return true;
    const dateValue = flow.settledAt || flow.createdAt;
    const timestamp = new Date(dateValue || '').getTime();
    if (Number.isNaN(timestamp)) return false;
    return timestamp >= incomeDateRange[0].startOf('day').valueOf()
      && timestamp <= incomeDateRange[1].endOf('day').valueOf();
  });

  const flowColumns: ColumnsType<UserProviderSettlementFlow> = [
    {
      title: '结算金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: number) => formatMoney(amount),
    },
    {
      title: '结算前',
      dataIndex: 'beforeAmount',
      key: 'beforeAmount',
      width: 120,
      render: (amount: number) => formatMoney(amount),
    },
    {
      title: '结算后',
      dataIndex: 'afterAmount',
      key: 'afterAmount',
      width: 120,
      render: (amount: number) => formatMoney(amount),
    },
    {
      title: '操作人员',
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 140,
      render: renderText,
    },
    {
      title: '结算备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
      render: renderText,
    },
    {
      title: '结算发票',
      dataIndex: 'invoiceUrl',
      key: 'invoiceUrl',
      width: 110,
      render: (url: string) => url ? <a href={normalizeFileUrl(url)} target="_blank" rel="noreferrer">查看</a> : '-',
    },
    {
      title: '结算时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: formatDateTime,
    },
  ];

  const incomeFlowColumns: ColumnsType<UserProviderOrderSettlementFlow> = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 170,
      render: renderText,
    },
    {
      title: '订单来源',
      dataIndex: 'orderSource',
      key: 'orderSource',
      width: 100,
      render: (value: string) => orderSourceTextMap[value] || value || '-',
    },
    {
      title: '美团预约单号',
      dataIndex: 'meituanReservationNo',
      key: 'meituanReservationNo',
      width: 160,
      render: renderText,
    },
    {
      title: '订单金额',
      dataIndex: 'orderAmount',
      key: 'orderAmount',
      width: 120,
      render: (value: number) => formatMoney(value),
    },
    {
      title: '收入金额',
      dataIndex: 'settlementAmount',
      key: 'settlementAmount',
      width: 120,
      render: (value: number) => <Text strong>{formatMoney(value)}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (value: string) => <Tag color={orderSettlementStatusColorMap[value] || 'default'}>{orderSettlementStatusTextMap[value] || value || '-'}</Tag>,
    },
    {
      title: '结算日期',
      dataIndex: 'availableAt',
      key: 'availableAt',
      width: 180,
      render: formatDateTime,
    },
    {
      title: '入账时间',
      dataIndex: 'settledAt',
      key: 'settledAt',
      width: 180,
      render: formatDateTime,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: formatDateTime,
    },
  ];

  const basicInfo = (
    <Card>
      <Descriptions bordered column={2}>
        <Descriptions.Item label="用户名">{renderText(currentUser?.username)}</Descriptions.Item>
        <Descriptions.Item label="姓名">{renderText(currentUser?.name)}</Descriptions.Item>
        <Descriptions.Item label="角色">{renderText(currentUser?.role)}</Descriptions.Item>
        <Descriptions.Item label="账号状态">
          {currentUser?.status === 'active' ? <Tag color="green">启用</Tag> : <Tag>禁用</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="电话">{renderText(currentUser?.phone)}</Descriptions.Item>
        <Descriptions.Item label="邮箱">{renderText(currentUser?.email)}</Descriptions.Item>
        <Descriptions.Item label="所属机构">{renderText(currentUser?.organization)}</Descriptions.Item>
        <Descriptions.Item label="最后登录">{renderText(currentUser?.lastLogin)}</Descriptions.Item>
      </Descriptions>
    </Card>
  );

  const providerInfo = (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Card title="账号信息">
        <Descriptions bordered column={2}>
          <Descriptions.Item label="用户名">{renderText(currentUser?.username)}</Descriptions.Item>
          <Descriptions.Item label="姓名">{renderText(currentUser?.name)}</Descriptions.Item>
          <Descriptions.Item label="电话">{renderText(currentUser?.phone)}</Descriptions.Item>
          <Descriptions.Item label="角色">{renderText(currentUser?.role)}</Descriptions.Item>
          <Descriptions.Item label="账号状态">
            {currentUser?.status === 'active' ? <Tag color="green">启用</Tag> : <Tag>禁用</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="可提现金额">
            <Text strong>{formatMoney(settlementSummary?.provider?.withdrawableAmount ?? currentUser?.withdrawableAmount)}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="代理商信息">
        <Descriptions bordered column={2}>
          <Descriptions.Item label="接单服务分类" span={2}>
            {serviceCategoryNames.length ? serviceCategoryNames.map(name => <Tag key={name}>{name}</Tag>) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="订单来源">
            <Space>
              {currentUser?.orderSourceWechat ? <Tag color="green">微信</Tag> : null}
              {currentUser?.orderSourceMeituan ? <Tag color="blue">美团</Tag> : null}
              {!currentUser?.orderSourceWechat && !currentUser?.orderSourceMeituan ? '-' : null}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="微信服务城市">
            {wechatRegions.length
              ? wechatRegions.map((region: any) => (
                <Tag key={region.regionId || `${region.provinceName}${region.cityName}${region.districtName}`}>
                  {[region.provinceName, region.cityName, region.districtName].filter(Boolean).join(' / ')}
                </Tag>
              ))
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="绑定美团门店" span={2}>
            {meituanShopNames.length ? meituanShopNames.map(name => <Tag key={name}>{name}</Tag>) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="营业执照">{renderFile(currentUser?.businessLicense)}</Descriptions.Item>
          <Descriptions.Item label="身份证正面">{renderFile(currentUser?.idCardFront)}</Descriptions.Item>
          <Descriptions.Item label="身份证反面">{renderFile(currentUser?.idCardBack)}</Descriptions.Item>
        </Descriptions>
      </Card>
    </Space>
  );

  return (
    <div>
      <Title level={2}>我的信息</Title>
      <Spin spinning={loading}>
        {currentUser?.isProvider ? (
          <Tabs
            items={[
              {
                key: 'profile',
                label: '个人信息',
                children: providerInfo,
              },
              {
                key: 'flows',
                label: '结算流水',
                children: (
                  <Card>
                    <Space style={{ marginBottom: 16 }}>
                      <Text>结算日期</Text>
                      <RangePicker
                        value={settlementDateRange}
                        onChange={(dates) => setSettlementDateRange(dates?.[0] && dates?.[1] ? [dates[0], dates[1]] : null)}
                      />
                      <Button onClick={() => setSettlementDateRange(null)}>重置</Button>
                    </Space>
                    <Table
                      columns={flowColumns}
                      dataSource={filteredSettlementFlows}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: 900 }}
                    />
                  </Card>
                ),
              },
              {
                key: 'incomeFlows',
                label: '收入流水',
                children: (
                  <Card>
                    <Space style={{ marginBottom: 16 }}>
                      <Text>收入日期</Text>
                      <RangePicker
                        value={incomeDateRange}
                        onChange={(dates) => setIncomeDateRange(dates?.[0] && dates?.[1] ? [dates[0], dates[1]] : null)}
                      />
                      <Button onClick={() => setIncomeDateRange(null)}>重置</Button>
                    </Space>
                    <Table
                      columns={incomeFlowColumns}
                      dataSource={filteredIncomeFlows}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      scroll={{ x: 1800 }}
                    />
                  </Card>
                ),
              },
            ]}
          />
        ) : basicInfo}
      </Spin>
    </div>
  );
};

export default MyInfo;
